import clsx from 'clsx';
import React from 'react';
import Button from '../../Components/Button';

type BuilderPanelProps = {
    title: string;
    description?: React.ReactNode;
    icon?: React.ReactNode;
    actions?: React.ReactNode;
    eyebrow?: string;
    className?: string;
    bodyClassName?: string;
    children: React.ReactNode;
};

type BuilderPillButtonProps = React.ComponentProps<typeof Button> & {
    active?: boolean;
    tone?: 'neutral' | 'accent' | 'danger';
    icon?: React.ReactNode;
    className?: string;
};

function BuilderPanel({
    title,
    description,
    icon,
    actions,
    eyebrow,
    className,
    bodyClassName,
    children,
}: BuilderPanelProps) {
    return (
        <section
            className={clsx(
                'rounded-2xl border border-slate-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.06)]',
                className,
            )}
        >
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-4 py-4 md:px-5">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        {icon && (
                            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                                {icon}
                            </span>
                        )}
                        <div className="min-w-0">
                            {eyebrow && (
                                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                    {eyebrow}
                                </p>
                            )}
                            <h3 className="text-base font-semibold text-slate-900">
                                {title}
                            </h3>
                        </div>
                    </div>
                    {description && (
                        <div className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                            {description}
                        </div>
                    )}
                </div>
                {actions && (
                    <div className="flex flex-wrap gap-2">{actions}</div>
                )}
            </div>
            <div className={clsx('px-4 py-4 md:px-5 md:py-5', bodyClassName)}>
                {children}
            </div>
        </section>
    );
}

function BuilderPillButton({
    active = false,
    tone = 'neutral',
    icon,
    className,
    children,
    ...props
}: BuilderPillButtonProps) {
    const hasLabel = Boolean(children);

    return (
        <Button
            className={clsx(
                'normal-case rounded-xl px-3 py-2 text-xs font-semibold tracking-[0.02em] shadow-none',
                tone === 'neutral' &&
                    (active
                        ? 'bg-primary text-white'
                        : 'border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'),
                tone === 'accent' &&
                    (active
                        ? 'bg-emerald-600 text-white'
                        : 'border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'),
                tone === 'danger' &&
                    'border border-red-100 bg-red-50 text-red-600 hover:bg-red-100',
                className,
            )}
            {...props}
        >
            <span
                className={clsx(
                    'flex items-center justify-center',
                    hasLabel && 'gap-2',
                )}
            >
                {icon && <span className="h-4 w-4">{icon}</span>}
                {hasLabel && <span>{children}</span>}
            </span>
        </Button>
    );
}

function BuilderMetaPill({
    children,
    className,
}: {
    children: React.ReactNode;
    className?: string;
}) {
    return (
        <span
            className={clsx(
                'inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[11px] font-medium text-slate-600',
                className,
            )}
        >
            {children}
        </span>
    );
}

export { BuilderMetaPill, BuilderPanel, BuilderPillButton };
